/**
 * CommandPalette — v3 (Spec §2.3, Mai 2026).
 *
 * Layout: Centered-Modal, 640px breit, max-height 480px, shadow-modal, radius-lg.
 *
 * Sektionen:
 *   1. Recent (max 5, nur ohne Query)
 *   2. Aktionen (Verbs first): Beleg hochladen, Approval erteilen für …,
 *      Payment-Run erstellen, Lieferant anlegen, Workflow erstellen,
 *      Export …, Theme wechseln (Sub-Picker)
 *   3. Navigation (Go-to …)
 *   4. Suche-Ergebnisse (live-async): Rechnungen mit Mini-Thumb, Lieferanten
 *
 * Sub-Pickers (Raycast-Pattern):
 *   - "Approval erteilen" → 2. Stufe Inbox-Filter (offene Tasks)
 *   - "Theme wechseln" → 2. Stufe Light / Dark / System
 *   - "Export …" → 2. Stufe CSV / Excel / SEPA-XML
 *
 * Wir nutzen Dialog + Command direkt (nicht CommandDialog), damit wir
 * die Modal-Breite (640px) + Shadow-Tokens kontrollieren können.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Command as CommandPrimitive } from "cmdk";
import {
  ArrowLeft,
  BarChart3,
  Building2,
  CheckCircle,
  Clock,
  CreditCard,
  Download,
  FileText,
  Inbox,
  Keyboard,
  LayoutDashboard,
  Monitor,
  Moon,
  Palette,
  Search,
  Settings,
  ShieldCheck,
  Sun,
  Upload,
  Wallet,
  Workflow,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";
import { useCommandStore } from "@/stores/command";
import { useShortcutsCheatsheet } from "@/components/shared";
import { useThemeStore, type Theme } from "@/stores/theme";
import { useMyApprovals } from "@/hooks/use-approvals";
import { apiClient, apiV1WsPath, AppError } from "@/lib/api";
import type { Invoice, Supplier } from "@/lib/types";
import { formatMoney } from "@/lib/money";
import { getRecentItems, pushRecentItem, type RecentItem } from "@/lib/recent";
import { cn } from "@/lib/utils";

type Stage = "root" | "pickApproval" | "pickTheme" | "pickExport";

/**
 * Lokale Modal-Hülle: 640px breit, max-h 480px, shadow-modal, radius-lg.
 * Nutzt direkt Dialog+Command statt der shadcn-CommandDialog-Komponente,
 * damit wir die Spec-Maße präzise treffen.
 */
function PaletteShell({
  open,
  onOpenChange,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "w-[640px] max-w-[calc(100vw-32px)] gap-0 overflow-hidden p-0",
          "rounded-lg border-border-default bg-bg-surface shadow-modal",
        )}
      >
        <DialogTitle className="sr-only">Befehlspalette</DialogTitle>
        <DialogDescription className="sr-only">
          Suche oder Aktion ausführen
        </DialogDescription>
        <CommandPrimitive
          className={cn(
            "flex max-h-[480px] w-full flex-col overflow-hidden",
            "[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2",
            "[&_[cmdk-group-heading]]:text-tiny [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.06em]",
            "[&_[cmdk-group-heading]]:text-text-tertiary",
            "[&_[cmdk-input]]:h-12",
            "[&_[cmdk-item]]:gap-2 [&_[cmdk-item]]:rounded-md [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-2",
            "[&_[cmdk-item][data-selected=true]]:bg-bg-active [&_[cmdk-item][data-selected=true]]:text-text-primary",
          )}
        >
          {children}
        </CommandPrimitive>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Einheitlicher Input-Header mit Search-Icon (kein Border-Bottom-Verlust
 * im Vergleich zum shadcn-CommandInput).
 */
function PaletteInput({
  value,
  onValueChange,
  placeholder,
  autoFocus,
}: {
  value: string;
  onValueChange: (v: string) => void;
  placeholder: string;
  autoFocus?: boolean;
}) {
  return (
    <div className="flex items-center gap-2 border-b border-border-subtle px-3">
      <Search className="h-4 w-4 shrink-0 text-text-tertiary" aria-hidden="true" />
      <CommandPrimitive.Input
        value={value}
        onValueChange={onValueChange}
        placeholder={placeholder}
        autoFocus={autoFocus}
        className={cn(
          "flex h-12 w-full bg-transparent py-3 text-body text-text-primary outline-none",
          "placeholder:text-text-tertiary disabled:cursor-not-allowed disabled:opacity-50",
        )}
      />
    </div>
  );
}

export function CommandPalette() {
  const open = useCommandStore((s) => s.open);
  const setOpen = useCommandStore((s) => s.setOpen);
  const navigate = useNavigate();
  const location = useLocation();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const base = `/workspaces/${workspaceId}`;

  const [stage, setStage] = useState<Stage>("root");
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [invoiceResults, setInvoiceResults] = useState<Invoice[]>([]);
  const [supplierResults, setSupplierResults] = useState<Supplier[]>([]);
  const [recent, setRecent] = useState<RecentItem[]>([]);
  const abortRef = useRef<AbortController | null>(null);

  const theme = useThemeStore((s) => s.theme);
  const setTheme = useThemeStore((s) => s.setTheme);
  const { setOpen: setCheatsheetOpen } = useShortcutsCheatsheet();
  const { data: approvals = [] } = useMyApprovals(workspaceId || null);

  // Debounce User-Input → 200ms.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 200);
    return () => clearTimeout(t);
  }, [query]);

  // Reset on open.
  useEffect(() => {
    if (open) {
      setRecent(getRecentItems());
      setQuery("");
      setStage("root");
    }
  }, [open]);

  // Server-Search.
  useEffect(() => {
    if (!workspaceId || stage !== "root") return;
    if (debouncedQuery.length < 2) {
      setInvoiceResults([]);
      setSupplierResults([]);
      return;
    }
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const q = debouncedQuery;

    async function runSearch() {
      try {
        const [inv, sup] = await Promise.all([
          apiClient
            .get<Invoice[]>(apiV1WsPath(workspaceId!, "invoices"), {
              query: { search: q, limit: 10 },
              signal: ctrl.signal,
            })
            .catch((e) => {
              if (
                e instanceof AppError &&
                (e.status === 401 ||
                  e.status === 0 ||
                  e.status === 404 ||
                  e.status === 503)
              ) {
                return [] as Invoice[];
              }
              throw e;
            }),
          apiClient
            .get<Supplier[]>(apiV1WsPath(workspaceId!, "suppliers/search"), {
              query: { q, limit: 10 },
              signal: ctrl.signal,
            })
            .catch((e) => {
              if (
                e instanceof AppError &&
                (e.status === 401 ||
                  e.status === 0 ||
                  e.status === 404 ||
                  e.status === 503)
              ) {
                return [] as Supplier[];
              }
              throw e;
            }),
        ]);
        if (!ctrl.signal.aborted) {
          setInvoiceResults(inv.slice(0, 10));
          setSupplierResults(sup.slice(0, 10));
        }
      } catch {
        /* ignore */
      }
    }

    runSearch();
    return () => ctrl.abort();
  }, [debouncedQuery, workspaceId, stage]);

  function run(fn: () => void) {
    setOpen(false);
    fn();
  }

  function goInvoice(i: Invoice) {
    pushRecentItem({
      id: i.id,
      type: "invoice",
      label: `${i.invoiceNumber} — ${i.supplierName}`,
      sub: i.status,
      href: `${base}/invoices/${i.id}`,
    });
    navigate(`${base}/invoices/${i.id}`);
  }

  function goSupplier(s: Supplier) {
    pushRecentItem({
      id: s.id,
      type: "supplier",
      label: s.name,
      sub: s.trustLevel,
      href: `${base}/suppliers/${s.id}`,
    });
    navigate(`${base}/suppliers/${s.id}`);
  }

  function openUploadDialog() {
    const search = new URLSearchParams(location.search);
    search.set("upload", "1");
    navigate(`${location.pathname}?${search.toString()}`);
  }

  function goExport(kind: "csv" | "excel" | "sepa") {
    const search = new URLSearchParams();
    search.set("export", kind);
    navigate(`${base}/invoices?${search.toString()}`);
  }

  // Approvals für Sub-Picker (top 15 offene).
  const topApprovals = useMemo(() => approvals.slice(0, 15), [approvals]);

  const hasQuery = debouncedQuery.length >= 2;
  const recentTop = useMemo(() => recent.slice(0, 5), [recent]);

  // ---------- Sub-Picker: "Approval erteilen für …" ----------
  if (stage === "pickApproval") {
    return (
      <PaletteShell open={open} onOpenChange={setOpen}>
        <SubHeader
          label="Approval auswählen"
          onBack={() => setStage("root")}
        />
        <PaletteInput
          value={query}
          onValueChange={setQuery}
          placeholder="Rechnung suchen…"
          autoFocus
        />
        <CommandList>
          <CommandEmpty>Keine offenen Approvals.</CommandEmpty>
          <CommandGroup heading="Offene Approvals">
            {topApprovals.length === 0 ? (
              <CommandItem disabled value="empty-approvals">
                <CheckCircle className="h-4 w-4" />
                <span className="text-text-tertiary">
                  Aktuell nichts in der Warteschlange.
                </span>
              </CommandItem>
            ) : (
              topApprovals.map((task) => (
                <CommandItem
                  key={task.id}
                  value={`${task.invoiceNumber ?? task.id} ${task.supplierName ?? ""}`}
                  onSelect={() =>
                    run(() => navigate(`${base}/inbox?task=${task.id}`))
                  }
                >
                  <CheckCircle className="h-4 w-4" />
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-small">
                      {task.invoiceNumber ?? `Task ${task.id.slice(0, 8)}`}
                      {task.supplierName ? ` — ${task.supplierName}` : ""}
                    </div>
                    {task.stepName && (
                      <div className="text-tiny text-text-tertiary">
                        {task.stepName}
                      </div>
                    )}
                  </div>
                </CommandItem>
              ))
            )}
          </CommandGroup>
        </CommandList>
      </PaletteShell>
    );
  }

  // ---------- Sub-Picker: "Theme wechseln" ----------
  if (stage === "pickTheme") {
    const themes: { value: Theme; label: string; icon: typeof Sun }[] = [
      { value: "light", label: "Hell", icon: Sun },
      { value: "dark", label: "Dunkel", icon: Moon },
      { value: "system", label: "System", icon: Monitor },
    ];
    return (
      <PaletteShell open={open} onOpenChange={setOpen}>
        <SubHeader label="Theme wählen" onBack={() => setStage("root")} />
        <PaletteInput
          value={query}
          onValueChange={setQuery}
          placeholder="Theme suchen…"
          autoFocus
        />
        <CommandList>
          <CommandEmpty>Kein Treffer.</CommandEmpty>
          <CommandGroup heading="Theme">
            {themes.map((t) => {
              const Icon = t.icon;
              const active = theme === t.value;
              return (
                <CommandItem
                  key={t.value}
                  value={`theme ${t.label}`}
                  onSelect={() => run(() => setTheme(t.value))}
                >
                  <Icon className="h-4 w-4" />
                  <span className="flex-1">{t.label}</span>
                  {active && (
                    <span className="text-tiny text-brand-600">aktiv</span>
                  )}
                </CommandItem>
              );
            })}
          </CommandGroup>
        </CommandList>
      </PaletteShell>
    );
  }

  // ---------- Sub-Picker: "Export …" ----------
  if (stage === "pickExport") {
    return (
      <PaletteShell open={open} onOpenChange={setOpen}>
        <SubHeader label="Export wählen" onBack={() => setStage("root")} />
        <PaletteInput
          value={query}
          onValueChange={setQuery}
          placeholder="Format suchen…"
          autoFocus
        />
        <CommandList>
          <CommandEmpty>Kein Treffer.</CommandEmpty>
          <CommandGroup heading="Format">
            <CommandItem
              value="export csv"
              onSelect={() => run(() => goExport("csv"))}
            >
              <Download className="h-4 w-4" />
              <span className="flex-1">CSV</span>
            </CommandItem>
            <CommandItem
              value="export excel xlsx"
              onSelect={() => run(() => goExport("excel"))}
            >
              <Download className="h-4 w-4" />
              <span className="flex-1">Excel (XLSX)</span>
            </CommandItem>
            <CommandItem
              value="export sepa xml"
              onSelect={() => run(() => goExport("sepa"))}
            >
              <Download className="h-4 w-4" />
              <span className="flex-1">SEPA-XML</span>
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </PaletteShell>
    );
  }

  // ---------- Root-Stage ----------
  return (
    <PaletteShell open={open} onOpenChange={setOpen}>
      <PaletteInput
        value={query}
        onValueChange={setQuery}
        placeholder="Suchen oder Aktion ausführen"
      />
      <CommandList>
        <CommandEmpty>Keine Treffer.</CommandEmpty>

        {/* 1. Recent (nur ohne Query) */}
        {!hasQuery && recentTop.length > 0 && (
          <>
            <CommandGroup heading="Zuletzt besucht">
              {recentTop.map((r) => (
                <CommandItem
                  key={`${r.type}:${r.id}`}
                  onSelect={() => run(() => navigate(r.href))}
                  value={r.label}
                >
                  <Clock className="h-4 w-4" />
                  <div className="flex-1 min-w-0">
                    <div className="truncate text-small">{r.label}</div>
                    {r.sub && (
                      <div className="text-tiny text-text-tertiary">
                        {r.sub}
                      </div>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {/* 2. Aktionen (Verbs first) */}
        <CommandGroup heading="Aktionen">
          <CommandItem
            onSelect={() => run(openUploadDialog)}
            value="beleg hochladen upload n"
          >
            <Upload className="h-4 w-4" />
            <span className="flex-1">Beleg hochladen</span>
            <CommandShortcut>N</CommandShortcut>
          </CommandItem>
          <CommandItem
            onSelect={() => setStage("pickApproval")}
            value="approval erteilen freigeben"
          >
            <CheckCircle className="h-4 w-4" />
            <span className="flex-1">Approval erteilen für …</span>
          </CommandItem>
          <CommandItem
            onSelect={() => run(() => navigate(`${base}/payment-runs/new`))}
            value="payment run erstellen"
          >
            <CreditCard className="h-4 w-4" />
            <span className="flex-1">Payment-Run erstellen</span>
          </CommandItem>
          <CommandItem
            onSelect={() => run(() => navigate(`${base}/suppliers/new`))}
            value="lieferant anlegen supplier"
          >
            <Building2 className="h-4 w-4" />
            <span className="flex-1">Lieferant anlegen</span>
          </CommandItem>
          <CommandItem
            onSelect={() => run(() => navigate(`${base}/workflows/new`))}
            value="workflow erstellen neu"
          >
            <Workflow className="h-4 w-4" />
            <span className="flex-1">Workflow erstellen</span>
          </CommandItem>
          <CommandItem
            onSelect={() => setStage("pickExport")}
            value="export csv excel sepa"
          >
            <Download className="h-4 w-4" />
            <span className="flex-1">Export …</span>
          </CommandItem>
          <CommandItem
            onSelect={() => setStage("pickTheme")}
            value="theme wechseln hell dunkel system"
          >
            <Palette className="h-4 w-4" />
            <span className="flex-1">Theme wechseln</span>
          </CommandItem>
          <CommandItem
            onSelect={() => run(() => setCheatsheetOpen(true))}
            value="tastenkürzel shortcuts hilfe"
          >
            <Keyboard className="h-4 w-4" />
            <span className="flex-1">Tastenkürzel anzeigen</span>
            <CommandShortcut>?</CommandShortcut>
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        {/* 3. Navigation */}
        <CommandGroup heading="Navigation">
          <CommandItem onSelect={() => run(() => navigate(`${base}/dashboard`))}>
            <LayoutDashboard className="h-4 w-4" />
            <span className="flex-1">Dashboard</span>
            <CommandShortcut>G D</CommandShortcut>
          </CommandItem>
          <CommandItem onSelect={() => run(() => navigate(`${base}/inbox`))}>
            <Inbox className="h-4 w-4" />
            <span className="flex-1">Inbox</span>
            <CommandShortcut>G I</CommandShortcut>
          </CommandItem>
          <CommandItem onSelect={() => run(() => navigate(`${base}/invoices`))}>
            <FileText className="h-4 w-4" />
            <span className="flex-1">Alle Rechnungen</span>
            <CommandShortcut>G R</CommandShortcut>
          </CommandItem>
          <CommandItem
            onSelect={() => run(() => navigate(`${base}/workflows`))}
          >
            <Workflow className="h-4 w-4" />
            <span className="flex-1">Workflows</span>
            <CommandShortcut>G W</CommandShortcut>
          </CommandItem>
          <CommandItem
            onSelect={() => run(() => navigate(`${base}/suppliers`))}
          >
            <Building2 className="h-4 w-4" />
            <span className="flex-1">Lieferanten</span>
            <CommandShortcut>G L</CommandShortcut>
          </CommandItem>
          <CommandItem
            onSelect={() => run(() => navigate(`${base}/payment-runs`))}
          >
            <CreditCard className="h-4 w-4" />
            <span className="flex-1">Payment-Runs</span>
            <CommandShortcut>G P</CommandShortcut>
          </CommandItem>
          <CommandItem
            onSelect={() => run(() => navigate(`${base}/bank-accounts`))}
          >
            <Wallet className="h-4 w-4" />
            <span className="flex-1">Bankkonten</span>
          </CommandItem>
          <CommandItem
            onSelect={() => run(() => navigate(`${base}/analytics`))}
          >
            <BarChart3 className="h-4 w-4" />
            <span className="flex-1">Analytics</span>
            <CommandShortcut>G A</CommandShortcut>
          </CommandItem>
          <CommandItem onSelect={() => run(() => navigate(`${base}/settings`))}>
            <Settings className="h-4 w-4" />
            <span className="flex-1">Einstellungen</span>
            <CommandShortcut>G S</CommandShortcut>
          </CommandItem>
          <CommandItem onSelect={() => run(() => navigate(`${base}/audit`))}>
            <ShieldCheck className="h-4 w-4" />
            <span className="flex-1">Audit-Log</span>
          </CommandItem>
        </CommandGroup>

        {/* 4. Suche-Ergebnisse (nur mit Query) */}
        {hasQuery && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Rechnungen">
              {invoiceResults.length === 0 ? (
                <CommandItem disabled value="no-invoice-matches">
                  <FileText className="h-4 w-4" />
                  <span className="text-text-tertiary">
                    Keine Rechnung gefunden
                  </span>
                </CommandItem>
              ) : (
                invoiceResults.map((i) => (
                  <CommandItem
                    key={i.id}
                    onSelect={() => run(() => goInvoice(i))}
                    value={`${i.invoiceNumber} ${i.supplierName}`}
                  >
                    {/* Mini-Thumb (Spec §2.3) — Platzhalter mit FileText-Icon
                     * im 32x32 Tile. */}
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm bg-bg-subtle text-text-tertiary">
                      <FileText className="h-4 w-4" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="truncate text-small">
                        {i.invoiceNumber} — {i.supplierName}
                      </div>
                      <div className="text-tiny text-text-tertiary">
                        {i.status}
                      </div>
                    </div>
                    <div className="font-mono text-tiny">
                      {formatMoney({
                        amount: i.amountGross,
                        currency: i.currency,
                      })}
                    </div>
                  </CommandItem>
                ))
              )}
            </CommandGroup>

            <CommandSeparator />

            <CommandGroup heading="Lieferanten">
              {supplierResults.length === 0 ? (
                <CommandItem disabled value="no-supplier-matches">
                  <Building2 className="h-4 w-4" />
                  <span className="text-text-tertiary">
                    Kein Lieferant gefunden
                  </span>
                </CommandItem>
              ) : (
                supplierResults.map((s) => (
                  <CommandItem
                    key={s.id}
                    onSelect={() => run(() => goSupplier(s))}
                    value={s.name}
                  >
                    <Building2 className="h-4 w-4" />
                    <span className="flex-1">{s.name}</span>
                    <span className="text-tiny text-text-tertiary">
                      {s.trustLevel}
                    </span>
                  </CommandItem>
                ))
              )}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </PaletteShell>
  );
}

/**
 * Sub-Picker-Header: "← Zurück  /  Sub-Title".
 */
function SubHeader({
  label,
  onBack,
}: {
  label: string;
  onBack: () => void;
}) {
  return (
    <div className="flex items-center gap-2 border-b border-border-subtle px-3 py-2">
      <button
        type="button"
        onClick={onBack}
        className={cn(
          "inline-flex items-center gap-1 rounded-sm px-2 py-1 text-small text-text-secondary",
          "transition-colors duration-fast ease-out-quint",
          "hover:bg-bg-hover hover:text-text-primary",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
        )}
        aria-label="Zurück"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Zurück
      </button>
      <span className="text-small text-text-tertiary">{label}</span>
    </div>
  );
}
